/**
 * Token refresh manager using event-based pattern
 *
 * This avoids mutable module state by using custom events for communication
 * between authenticatedFetch and AuthContext. This pattern is:
 * - Easier to test (no module-level state to mock)
 * - More explicit about the dependency relationship
 * - Avoids potential race conditions from setting module state
 */

const TOKEN_REFRESH_EVENT = "auth:token_refresh_request";

interface TokenRefreshRequestDetail {
    resolve: (token: string) => void;
    reject: (error: Error) => void;
}

/**
 * Request a token refresh. Returns a promise that resolves with the new access token.
 * This is called by authenticatedFetch when a token refresh is needed.
 */
export function requestTokenRefresh(): Promise<string> {
    return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            reject(new Error("Token refresh timeout - no handler registered"));
        }, 60000);

        const detail: TokenRefreshRequestDetail = {
            resolve: (token: string) => {
                clearTimeout(timeoutId);
                resolve(token);
            },
            reject: (error: Error) => {
                clearTimeout(timeoutId);
                reject(error);
            },
        };

        window.dispatchEvent(
            new CustomEvent(TOKEN_REFRESH_EVENT, { detail }),
        );
    });
}

/**
 * Subscribe to token refresh requests. Called by AuthContext.
 * Deduplicates concurrent refresh requests so only one network call is made.
 * Returns an unsubscribe function.
 */
export function subscribeToRefreshRequests(
    handler: () => Promise<string>,
): () => void {
    let inflightRefresh: Promise<string> | null = null;

    const listener = async (event: Event) => {
        const { resolve, reject } = (event as CustomEvent<TokenRefreshRequestDetail>).detail;
        try {
            if (!inflightRefresh) {
                inflightRefresh = handler().finally(() => {
                    inflightRefresh = null;
                });
            }
            const newToken = await inflightRefresh;
            resolve(newToken);
        } catch (error) {
            reject(error instanceof Error ? error : new Error(String(error)));
        }
    };

    window.addEventListener(TOKEN_REFRESH_EVENT, listener);
    return () => window.removeEventListener(TOKEN_REFRESH_EVENT, listener);
}
